/**
 * Periodontograma: el registro periodontal de toda la boca. Por cada diente permanente se anotan seis sitios
 * (tres por vestibular y tres por palatino o lingual: mesial, central y distal) con la profundidad de sondaje,
 * la posición del margen gingival (recesión), sangrado al sondaje, placa y supuración; y por diente la movilidad y la furca.
 * El nivel de inserción clínica (NIC) = profundidad de sondaje + recesión. Es un registro: el diagnóstico es del profesional.
 */

/** Dientes permanentes en el orden en que se dibuja el periodontograma (de la derecha a la izquierda del paciente, como el odontograma). */
export const DIENTES_SUPERIORES = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28] as const;
export const DIENTES_INFERIORES = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38] as const;
export const DIENTES_PERIO: readonly number[] = [...DIENTES_SUPERIORES, ...DIENTES_INFERIORES];

export interface SitioPerio {
  /** profundidad de sondaje en mm (0 a 15); null = sin medir */
  pd: number | null;
  /** recesión en mm: positivo = el margen está por debajo del límite amelocementario; negativo = por encima (agrandamiento). null = sin medir */
  rec: number | null;
  sang: boolean; placa: boolean; sup: boolean;
}
export interface DientePerio {
  ausente: boolean;
  /** movilidad 0 a 3 */
  mov: number;
  /** furca 0 a 3 (solo molares) */
  furca: number;
  /** tres sitios por vestibular y tres por palatino/lingual: mesial, central, distal */
  v: SitioPerio[]; l: SitioPerio[];
}
export type DatosPerio = Record<string, DientePerio>;

export const sitioVacio = (): SitioPerio => ({ pd: null, rec: null, sang: false, placa: false, sup: false });
export const dientePerioVacio = (): DientePerio => ({ ausente: false, mov: 0, furca: 0, v: [sitioVacio(), sitioVacio(), sitioVacio()], l: [sitioVacio(), sitioVacio(), sitioVacio()] });
export const periodontogramaVacio = (): DatosPerio => Object.fromEntries(DIENTES_PERIO.map((n) => [String(n), dientePerioVacio()]));

/** Los molares son los únicos con furca. */
export const tieneFurca = (n: number) => [6, 7, 8].includes(n % 10);

const entero = (v: unknown, min: number, max: number): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};
const sitio = (v: unknown): SitioPerio => {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return { pd: entero(o.pd, 0, 15), rec: entero(o.rec, -5, 15), sang: o.sang === true, placa: o.placa === true, sup: o.sup === true };
};
const tres = (v: unknown): SitioPerio[] => [0, 1, 2].map((i) => sitio(Array.isArray(v) ? v[i] : null));

/** Deja solo lo válido: dientes que existen, valores en rango, tres sitios por cara. Lo que no viene se toma como vacío. */
export function limpiarPeriodontograma(entrada: unknown): DatosPerio {
  const o = (entrada && typeof entrada === "object" ? entrada : {}) as Record<string, unknown>;
  const salida: DatosPerio = {};
  for (const n of DIENTES_PERIO) {
    const d = (o[String(n)] && typeof o[String(n)] === "object" ? o[String(n)] : {}) as Record<string, unknown>;
    const ausente = d.ausente === true;
    salida[String(n)] = {
      ausente, mov: ausente ? 0 : entero(d.mov, 0, 3) ?? 0, furca: ausente || !tieneFurca(n) ? 0 : entero(d.furca, 0, 3) ?? 0,
      v: ausente ? [sitioVacio(), sitioVacio(), sitioVacio()] : tres(d.v), l: ausente ? [sitioVacio(), sitioVacio(), sitioVacio()] : tres(d.l),
    };
  }
  return salida;
}

export function problemasPeriodontograma(d: DatosPerio): string[] {
  const e: string[] = [];
  const sitios = DIENTES_PERIO.flatMap((n) => (d[String(n)] ? [...d[String(n)].v, ...d[String(n)].l] : []));
  if (!sitios.some((s) => s.pd !== null)) e.push("Anota la profundidad de sondaje de al menos un sitio.");
  if (DIENTES_PERIO.every((n) => d[String(n)]?.ausente)) e.push("Marca al menos un diente presente.");
  return e;
}

/** Nivel de inserción clínica de un sitio (mm), o null si falta la profundidad o la recesión. */
export const nic = (s: SitioPerio): number | null => (s.pd === null || s.rec === null ? null : s.pd + s.rec);

export interface ResumenPerio {
  dientes: number; sitios: number;
  pdMedia: number | null; pdMax: number | null; nicMedia: number | null; nicMax: number | null;
  sitios4: number; sitios6: number;
  pctSangrado: number | null; pctPlaca: number | null; supuracion: number;
  conMovilidad: number; conFurca: number;
}

const media = (x: number[]) => (x.length ? Math.round((x.reduce((a, b) => a + b, 0) / x.length) * 10) / 10 : null);

export function resumenPeriodontograma(d: DatosPerio): ResumenPerio {
  const presentes = DIENTES_PERIO.filter((n) => d[String(n)] && !d[String(n)].ausente);
  const sitios = presentes.flatMap((n) => [...d[String(n)].v, ...d[String(n)].l]);
  const pds = sitios.flatMap((s) => (s.pd === null ? [] : [s.pd]));
  const nics = sitios.flatMap((s) => { const x = nic(s); return x === null ? [] : [x]; });
  // el sangrado y la placa se cuentan sobre los sitios de los dientes presentes que ya se midieron (tienen profundidad)
  const medidos = sitios.filter((s) => s.pd !== null);
  const pct = (cuantos: number) => (medidos.length ? Math.round((cuantos / medidos.length) * 100) : null);
  return {
    dientes: presentes.length, sitios: pds.length,
    pdMedia: media(pds), pdMax: pds.length ? Math.max(...pds) : null, nicMedia: media(nics), nicMax: nics.length ? Math.max(...nics) : null,
    sitios4: pds.filter((x) => x >= 4).length, sitios6: pds.filter((x) => x >= 6).length,
    pctSangrado: pct(medidos.filter((s) => s.sang).length), pctPlaca: pct(medidos.filter((s) => s.placa).length), supuracion: sitios.filter((s) => s.sup).length,
    conMovilidad: presentes.filter((n) => d[String(n)].mov > 0).length, conFurca: presentes.filter((n) => d[String(n)].furca > 0).length,
  };
}

/** Texto corto del resumen, para listas y para la evolución. */
export function textoResumenPerio(r: ResumenPerio): string {
  const p = [`${r.dientes} dientes · ${r.sitios} sitios medidos`];
  if (r.pdMedia !== null) p.push(`sondaje medio ${r.pdMedia} mm (máx. ${r.pdMax})`);
  if (r.nicMedia !== null) p.push(`NIC medio ${r.nicMedia} mm`);
  if (r.sitios4) p.push(`${r.sitios4} sitios ≥ 4 mm${r.sitios6 ? ` (${r.sitios6} ≥ 6 mm)` : ""}`);
  if (r.pctSangrado !== null) p.push(`sangrado ${r.pctSangrado} %`);
  if (r.pctPlaca !== null) p.push(`placa ${r.pctPlaca} %`);
  if (r.supuracion) p.push(`${r.supuracion} con supuración`);
  if (r.conMovilidad) p.push(`${r.conMovilidad} con movilidad`);
  if (r.conFurca) p.push(`${r.conFurca} con furca`);
  return p.join("; ") + ".";
}

/** Cambio entre dos periodontogramas (el anterior y el nuevo): positivo = empeoró. Solo lo que se puede comparar. */
export function compararPerio(antes: ResumenPerio, ahora: ResumenPerio) {
  const dif = (a: number | null, b: number | null) => (a === null || b === null ? null : Math.round((b - a) * 10) / 10);
  return { pdMedia: dif(antes.pdMedia, ahora.pdMedia), nicMedia: dif(antes.nicMedia, ahora.nicMedia), pctSangrado: dif(antes.pctSangrado, ahora.pctSangrado), pctPlaca: dif(antes.pctPlaca, ahora.pctPlaca), sitios4: ahora.sitios4 - antes.sitios4 };
}
